/**
 * Overlay layout parts (ADR-114 §Decisión 3).
 *
 * DialogHeader/DialogFooter/DialogTitle/DialogDescription keep the class
 * contract used by the admin-console primitives so existing consumers do not
 * need changes. OverlayHeader/OverlayBody/OverlayFooter are the canonical
 * anatomy for new overlays (use with `className="p-0"` on the content root).
 */

import {
  cloneElement,
  isValidElement,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../../../lib/utils';

interface AsChildProps {
  asChild?: boolean;
}

function renderAsChild(
  asChild: boolean | undefined,
  children: ReactNode,
  classes: string,
): ReactElement | null {
  if (!asChild || !isValidElement(children)) return null;
  const child = children as ReactElement<{ className?: string }>;
  return cloneElement(child, { className: cn(classes, child.props.className) });
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex shrink-0 flex-col space-y-1.5 text-center sm:text-left', className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2',
        className,
      )}
      {...props}
    />
  );
}

export interface DialogTitleProps extends HTMLAttributes<HTMLHeadingElement>, AsChildProps {
  children: ReactNode;
}

export function DialogTitle({ className, children, asChild, ...props }: DialogTitleProps) {
  const classes = cn('text-lg font-semibold leading-none tracking-tight', className);
  const asChildNode = renderAsChild(asChild, children, classes);
  if (asChildNode) return asChildNode;

  return (
    <h2 className={classes} {...props}>
      {children}
    </h2>
  );
}

export interface DialogDescriptionProps
  extends HTMLAttributes<HTMLParagraphElement>,
    AsChildProps {
  children: ReactNode;
}

export function DialogDescription({
  className,
  children,
  asChild,
  ...props
}: DialogDescriptionProps) {
  const classes = cn('text-sm text-gray-500 dark:text-gray-400', className);
  const asChildNode = renderAsChild(asChild, children, classes);
  if (asChildNode) return asChildNode;

  return (
    <p className={classes} {...props}>
      {children}
    </p>
  );
}

/** Canonical overlay header (sticky by layout: shrink-0 in a flex column). */
export function OverlayHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <DialogHeader className={className} {...props} />;
}

/** Canonical overlay body: the only scrollable region of the overlay. */
export function OverlayBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto', className)} {...props} />;
}

/** Canonical overlay footer (sticky by layout: shrink-0 in a flex column). */
export function OverlayFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <DialogFooter className={className} {...props} />;
}
